import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import EventIcon from '@mui/icons-material/Event';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { complianceService, ComplianceDashboard as ComplianceDashboardType, SalesTaxReturn, ComplianceFiling, GDPRRequest } from '../api/services/compliance';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ComplianceDashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<ComplianceDashboardType | null>(null);
  const [salesTaxReturns, setVatReturns] = useState<SalesTaxReturn[]>([]);
  const [filings, setFilings] = useState<ComplianceFiling[]>([]);
  const [gdprRequests, setGdprRequests] = useState<GDPRRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardData, vatData, filingsData, gdprData] = await Promise.all([
        complianceService.getDashboard(30),
        complianceService.getSalesTaxReturns({ limit: 50 }),
        complianceService.getFilings({ limit: 50 }),
        complianceService.getGDPRRequests({ limit: 50 }),
      ]);

      setDashboard(dashboardData);
      setVatReturns(vatData.returns);
      setFilings(filingsData.filings);
      setGdprRequests(gdprData.requests);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load compliance data');
      console.error('Error fetching compliance data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
      case 'accepted':
      case 'completed':
        return 'success';
      case 'pending':
      case 'in_progress':
        return 'warning';
      case 'overdue':
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error';
      case 'normal':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboard) return null;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <GavelIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Compliance Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              US regulatory compliance, Sales Tax returns, and CCPA management
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Compliance Score Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography variant="h2" fontWeight="bold">
                  {dashboard.dashboard.compliance_score.overall}%
                </Typography>
                <Typography variant="h6">Overall Compliance Score</Typography>
                <Chip
                  label={dashboard.dashboard.compliance_score.status}
                  sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Sales Tax Compliance</Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <LinearProgress
                        variant="determinate"
                        value={dashboard.dashboard.compliance_score.vat_compliance}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)' }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {dashboard.dashboard.compliance_score.vat_compliance}%
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Filing Compliance</Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <LinearProgress
                        variant="determinate"
                        value={dashboard.dashboard.compliance_score.filing_compliance}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)' }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {dashboard.dashboard.compliance_score.filing_compliance}%
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>GDPR Compliance</Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <LinearProgress
                        variant="determinate"
                        value={dashboard.dashboard.compliance_score.gdpr_compliance}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)' }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {dashboard.dashboard.compliance_score.gdpr_compliance}%
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box flexGrow={1}>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.vat_returns.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sales Tax Returns
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={`${dashboard.dashboard.vat_returns.submitted} Submitted`} size="small" color="success" />
                    <Chip label={`${dashboard.dashboard.vat_returns.pending} Pending`} size="small" color="warning" />
                    {dashboard.dashboard.vat_returns.overdue > 0 && (
                      <Chip label={`${dashboard.dashboard.vat_returns.overdue} Overdue`} size="small" color="error" />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <AssignmentIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Box flexGrow={1}>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.filings.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compliance Filings
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={`${dashboard.dashboard.filings.submitted} Submitted`} size="small" color="success" />
                    <Chip label={`${dashboard.dashboard.filings.upcoming_count} Upcoming`} size="small" color="info" />
                    {dashboard.dashboard.filings.overdue > 0 && (
                      <Chip label={`${dashboard.dashboard.filings.overdue} Overdue`} size="small" color="error" />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PrivacyTipIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                <Box flexGrow={1}>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.gdpr_requests.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    GDPR Requests
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={`${dashboard.dashboard.gdpr_requests.completed} Completed`} size="small" color="success" />
                    <Chip label={`${dashboard.dashboard.gdpr_requests.in_progress} In Progress`} size="small" color="info" />
                    {dashboard.dashboard.gdpr_requests.overdue > 0 && (
                      <Chip label={`${dashboard.dashboard.gdpr_requests.overdue} Overdue`} size="small" color="error" />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Avg Response: {dashboard.dashboard.gdpr_requests.average_response_days.toFixed(1)} days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Sales Tax Returns" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab label="Compliance Filings" icon={<AssignmentIcon />} iconPosition="start" />
          <Tab label="GDPR Requests" icon={<PrivacyTipIcon />} iconPosition="start" />
          <Tab label="Upcoming Deadlines" icon={<EventIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>Sales Tax Returns</Typography>
          {/* Sales Tax Returns table will be added */}
          <Typography color="text.secondary">Sales Tax Returns table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>Compliance Filings</Typography>
          {/* Filings table will be added */}
          <Typography color="text.secondary">Compliance Filings table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>GDPR Data Subject Requests</Typography>
          {/* GDPR table will be added */}
          <Typography color="text.secondary">GDPR Requests table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>Upcoming Compliance Deadlines</Typography>
          {/* Deadlines will be added */}
          <Typography color="text.secondary">Upcoming Deadlines coming soon...</Typography>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ComplianceDashboard;

